import type { DebounceSettings } from 'lodash';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef } from 'react';

import { DEBOUNCE_TIME_IN_MS } from '@/lib/const';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type noop = (...args: any[]) => any;

export function useDebounceFn<T extends noop>(
  fn: T,
  wait: number = DEBOUNCE_TIME_IN_MS,
  options?: DebounceSettings,
) {
  const ref = useRef(fn);
  ref.current = fn;

  const debounced = useMemo(
    () =>
      debounce(
        (...args: Parameters<T>): ReturnType<T> => {
          return ref.current(...args);
        },
        wait,
        options,
      ),
    [],
  );

  useEffect(
    () => () => {
      debounced.cancel();
    },
    [],
  );

  return {
    run: debounced,
    cancel: debounced.cancel,
    flush: debounced.flush,
  };
}
